import styles from "./insurance-finances.module.css";
import {
  Locale,
  type LocalizedStrings,
  useLocalization,
} from "~/components/localization/LocalizationProvider";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";

type Localization = {
  title: string;
  perfectSmileHeading: string;
  perfectSmileP1: string;
  perfectSmileP2: string;
  perfectSmileP3: string;
  perfectSmileP4: string;
  perfectSmileP5: string;
  perfectSmileP6: string;
  financialOptionsHeading: string;
  financialOptionsP1: string;
  financialOptions: string[];
  insuranceHeading: string;
  insuranceP1: string;
  insuranceP2: string;
  insuranceP3: string;
  acceptedInsuranceHeading: string;
  acceptedInsuranceP1: string;
  acceptedInsuranceList: string[];
};

const englishLocalization: Localization = {
  title: "Insurance & Finances",
  perfectSmileHeading: "We Make That Perfect Smile Affordable!",
  perfectSmileP1:
    "Your smile plays a major role in the impression you make on the people around you. A decision to pursue orthodontic treatment is an important investment in yourself and your future.",
  perfectSmileP2:
    "The cost of orthodontic treatment can vary depending on your individual needs and treatment plan. We offer a variety of flexible payment plans to help make your orthodontic care affordable. To help you achieve your dream of a beautiful smile, we will work with you to create a payment plan that fits your budget.",
  perfectSmileP3:
    "We want to fill Miami, FL with beautiful smiles, so we make every effort to make sure your treatment fits your family's budget.",
  perfectSmileP4:
    "There’s never been a better time to consider orthodontic treatment, and we're here to discuss financial arrangements with you.",
  perfectSmileP5:
    "Your fees for orthodontic treatment depend on your unique treatment plan. Once we determine your treatment plan, our financial experts will cover all of the details of your investment.",
  perfectSmileP6:
    "It's important to Dr. Garcia-Paul that payment does not become a barrier to achieving the smile of your dreams, so we’ll work with you to come up with a payment plan that works for you.",
  financialOptionsHeading: "More Financial Options",
  financialOptionsP1: "Here are some more financial benefits to choose from:",
  financialOptions: [
    "If you decide to pay for your treatment in full at the beginning of your treatment, you can get a discount.",
    "You can opt-in to our flexible, in-office financing plan. You’ll enjoy interest-free payments after an initial down payment at the beginning of your treatment.",
    "You can also discuss additional payment options through third-party financing companies. If you're approved, you may be able to pay low monthly payments without a down payment.",
  ],
  insuranceHeading: "How Does Insurance Work With Orthodontics?",
  insuranceP1:
    "Our practice accepts most major insurance plans. Insurance policies usually have a lifetime orthodontic benefit that can be very different from regular dental insurance coverage.",
  insuranceP2:
    "Our financial coordinator will work with you to help you fully understand your orthodontic benefits. For your convenience, she can complete all paperwork and file the claim on your behalf.",
  insuranceP3:
    "We’ll also file your benefit on your behalf and help maximize your benefit. If your insurance provider isn't listed, please call us and we'll do everything we can to help.",
  acceptedInsuranceHeading: "We Accept Insurance",
  acceptedInsuranceP1: "Here are just some of the insurance plans we accept:",
  acceptedInsuranceList: [
    "AETNA (PPO)",
    "AMERIGROUP",
    "AMERITAS Group",
    "BLUE CROSS BLUE SHIELD",
    "ASSURANT",
    "CIGNA (PPO)",
    "DELTA DENTAL",
    "FLORIDA COMBINED LIFE",
    "GUARDIAN",
    "HUMANA",
    "LINCOLN FINANCIAL GROUP",
    "METLIFE PPO",
    "PRINCIPAL LIFE INSURANCE",
    "SOLSTICE / COVENTRY",
    "UNITED CONCORDIA",
    "UNITED HEALTHCARE",
  ],
} as const;

const spanishLocalization: Localization = {
  title: "Seguros y finanzas",
  perfectSmileHeading: "¡Hacemos que esa sonrisa perfecta sea asequible!",
  perfectSmileP1:
    "Tu sonrisa juega un papel importante en la impresión que causas en las personas que te rodean. La decisión de iniciar un tratamiento de ortodoncia es una inversión importante en ti y en tu futuro.",
  perfectSmileP2:
    "El costo del tratamiento de ortodoncia puede variar según tus necesidades individuales y tu plan de tratamiento. Ofrecemos una variedad de planes de pago flexibles para que tu atención de ortodoncia sea asequible. Para ayudarte a lograr tu sueño de tener una sonrisa hermosa, trabajaremos contigo para crear un plan de pago que se ajuste a tu presupuesto.",
  perfectSmileP3:
    "Queremos llenar Miami, FL de sonrisas hermosas, por eso hacemos todo lo posible para que tu tratamiento se ajuste al presupuesto de tu familia.",
  perfectSmileP4:
    "Nunca ha habido un mejor momento para considerar un tratamiento de ortodoncia, y estamos aquí para hablar contigo sobre las opciones financieras.",
  perfectSmileP5:
    "Los honorarios del tratamiento de ortodoncia dependen de tu plan de tratamiento único. Una vez que determinemos tu plan de tratamiento, nuestros expertos financieros revisarán todos los detalles de tu inversión.",
  perfectSmileP6:
    "Para la Dra. García-Paul es importante que el pago no se convierta en una barrera para lograr la sonrisa de tus sueños, por eso trabajaremos contigo para crear un plan de pago que funcione para ti.",
  financialOptionsHeading: "Más opciones financieras",
  financialOptionsP1:
    "Estos son algunos beneficios financieros adicionales para elegir:",
  financialOptions: [
    "Si decides pagar tu tratamiento en su totalidad al comienzo, puedes recibir un descuento.",
    "Puedes optar por nuestro plan de financiamiento flexible en el consultorio. Disfrutarás de pagos sin intereses después de un pago inicial al comienzo de tu tratamiento.",
    "También puedes hablar sobre opciones de pago adicionales a través de compañías de financiamiento externas. Si te aprueban, es posible que puedas hacer pagos mensuales bajos sin pago inicial.",
  ],
  insuranceHeading: "¿Cómo funciona el seguro con la ortodoncia?",
  insuranceP1:
    "Nuestro consultorio acepta la mayoría de los principales planes de seguro. Las pólizas de seguro suelen tener un beneficio de ortodoncia de por vida que puede ser muy diferente de la cobertura dental regular.",
  insuranceP2:
    "Nuestra coordinadora financiera trabajará contigo para ayudarte a comprender completamente tus beneficios de ortodoncia. Para tu comodidad, ella puede completar todos los documentos y presentar el reclamo en tu nombre.",
  insuranceP3:
    "También presentaremos tu beneficio en tu nombre y te ayudaremos a maximizarlo. Si tu proveedor de seguro no aparece en la lista, llámanos y haremos todo lo posible para ayudarte.",
  acceptedInsuranceHeading: "Aceptamos seguros",
  acceptedInsuranceP1:
    "Estos son solo algunos de los planes de seguro que aceptamos:",
  acceptedInsuranceList: [
    "AETNA (PPO)",
    "AMERIGROUP",
    "AMERITAS Group",
    "BLUE CROSS BLUE SHIELD",
    "ASSURANT",
    "CIGNA (PPO)",
    "DELTA DENTAL",
    "FLORIDA COMBINED LIFE",
    "GUARDIAN",
    "HUMANA",
    "LINCOLN FINANCIAL GROUP",
    "METLIFE PPO",
    "PRINCIPAL LIFE INSURANCE",
    "SOLSTICE / COVENTRY",
    "UNITED CONCORDIA",
    "UNITED HEALTHCARE",
  ],
} as const;

const localized: LocalizedStrings<Localization> = {
  [Locale.EN]: englishLocalization,
  [Locale.ES]: spanishLocalization,
};

export function InsuranceFinancesPage() {
  const getLocalization = useLocalization(localized);
  return (
    <>
      <HeroStyle2
        showFullImageOnMobile
        heroImageUrl="/insurances-finances-hero.jpg"
        title={getLocalization("title")}
        backgroundPosition="center"
      />
      <Flex
        direction="column"
        align="center"
        className={`section ${styles.mainContentSectionOuter}`}
      >
        <Flex
          width="100%"
          gap="8"
          direction="column"
          px="4"
          className={styles.mainContentSection}
        >
          <Flex width="100%" gap="5" direction="column">
            <Heading as="h2" className={styles.heading}>
              {getLocalization("perfectSmileHeading")}
            </Heading>
            <Text size="5">{getLocalization("perfectSmileP1")}</Text>
            <Text size="5">{getLocalization("perfectSmileP2")}</Text>
            <Text size="5">{getLocalization("perfectSmileP3")}</Text>
            <Text size="5">{getLocalization("perfectSmileP4")}</Text>
            <Text size="5">{getLocalization("perfectSmileP5")}</Text>
            <Text size="5">{getLocalization("perfectSmileP6")}</Text>
          </Flex>
          <Flex width="100%" gap="5" direction="column">
            <Heading as="h3" className={styles.subsectionHeading}>
              {getLocalization("financialOptionsHeading")}
            </Heading>
            <Text size="5">{getLocalization("financialOptionsP1")}</Text>
            <Text size="5">
              <Flex asChild direction="column" gap="2">
                <ul>
                  {getLocalization("financialOptions").map((option) => (
                    <li key={option}>{option}</li>
                  ))}
                </ul>
              </Flex>
            </Text>
          </Flex>
          <Flex width="100%" gap="5" direction="column">
            <Heading as="h4" className={styles.heading3}>
              {getLocalization("insuranceHeading")}
            </Heading>
            <Text size="5">{getLocalization("insuranceP1")}</Text>
            <Text size="5">{getLocalization("insuranceP2")}</Text>
            <Text size="5">{getLocalization("insuranceP3")}</Text>
          </Flex>
          <Flex width="100%" gap="5" direction="column">
            <Heading as="h4" className={styles.heading3}>
              {getLocalization("acceptedInsuranceHeading")}
            </Heading>
            <Text size="5">{getLocalization("acceptedInsuranceP1")}</Text>
            <Text size="3">
              <Flex asChild direction="column" gap="1">
                <ul>
                  {getLocalization("acceptedInsuranceList").map(
                    (option) => <li key={option}>{option}</li>
                  )}
                </ul>
              </Flex>
            </Text>
          </Flex>
        </Flex>
      </Flex>
      <Box className={styles.form}>
        <HomeConsultForm />
      </Box>
    </>
  );
}
